/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC } from "react"
import {
  Row,
  Col,
  Dropdown,
  Card,
  Button,
  Select,
  Checkbox,
} from "@duxton-web/ui"
import { MoreVerticalUniversal, EditableFilled } from "@duxton-web/icons"
import { useSelector, shallowEqual } from "react-redux"
import classnames from "classnames"

import { AppState } from "store/store.type"
import { SortableItemList, OverflowTip } from "components/common"
import { Modifier } from "types/models/menu"
import { getCurrencyInfo } from "utils/currency"
import { useTranslationsMap } from "common/utils/i18n"
import { getUCMConfig } from "utils/config"
import { selectMultiple, rearrange } from "utils/modifier_group_helper"
import useInventoryContext from "hooks/useInventoryContext"
import useMenuContext from "hooks/useMenuContext"
import commonStyles from "styles/common.modules.css"
import { antSelectedRed } from "./ModifierGroups.styles"
import { ModifierListLoading } from "./ModifierListLoading"

const Option = Select.Option

const rowHeight = 96

interface ModifierListProps {
  modifiers: Modifier[]
  checkedModifiers: Modifier[]
  showCheckbox: boolean | undefined
  onClickEditModifier: any
  onToggleShowCheckboxModifier: any
  onChangeModifierCheckbox: any
  onToggleShowSortOrderModifier: any
  onChangeModifierAvailability: any
  onSort(newList: Modifier[]): void
  showEditBtn: boolean | undefined
  isLoading?: boolean
}

const ModifierList: FC<ModifierListProps> = ({
  modifiers,
  checkedModifiers,
  showCheckbox,
  onClickEditModifier,
  onToggleShowCheckboxModifier,
  onChangeModifierCheckbox,
  onToggleShowSortOrderModifier,
  onChangeModifierAvailability,
  onSort,
  showEditBtn,
  isLoading = false,
}: ModifierListProps) => {
  const {
    outlet,
    outletV2,
    isEditAllowed,
    isStatusChangeAllowed,
    isSellingTimeStructure,
    enableTroyNewMerchantEndpoint,
  } = useSelector((state: AppState) => state?.menu, shallowEqual)
  const outletCurrency = enableTroyNewMerchantEndpoint
    ? outletV2?.currency?.code
    : outlet?.currency
  const currency = getCurrencyInfo(outletCurrency)

  const handleChangeModifierAvailability = (
    val: number,
    modifier: Modifier,
  ) => {
    onChangeModifierAvailability(modifier.modifierID, val)
  }

  const textMap = useTranslationsMap({
    header: "menu.modifierGroup.modifier.header",
    rearrange: "menu.modifierGroup.rearrangeModifier",
    selectMultiple: "menu.actions.selectMultiple",
    available: "menu.item.statuses.available",
    unavailableToday: "menu.item.statuses.unavailableUntilTomorrow",
    unavailableIndefinitely: "menu.item.statuses.unavailableIndefinitely",
    edit: "menu.modifierGroup.editOptionGroup",
  })

  const { disableAvailability } = useInventoryContext()
  const { isServiceBasedMenuEnabled } = useMenuContext()

  const renderRow = (modifier: Modifier, index: number) => {
    const isAvailable = modifier.availableStatus === 1

    return (
      <Row wrap={false} gutter={16}>
        <Col flex="auto">
          <OverflowTip
            className={classnames("name", !isAvailable && "unavailable")}
          >
            {modifier?.modifierName}
          </OverflowTip>
          <OverflowTip
            className={classnames(
              "OverflowTiprice",
              isAvailable && "unavailable",
            )}
          >
            {(isServiceBasedMenuEnabled
              ? modifier?.priceRange
              : modifier?.priceDisplay) ??
              `${currency?.symbol}0${currency?.separator}00`}
          </OverflowTip>
        </Col>
        {!disableAvailability && (
          <Col span={6}>
            <div onClick={e => e.stopPropagation()}>
              <Select
                data-testid="modifier-availability-selector"
                defaultValue={modifier?.availableStatus}
                onChange={(val: number) =>
                  handleChangeModifierAvailability(val, modifier)
                }
                css={css`
                  width: 100%;
                  ${!isAvailable && antSelectedRed}
                `}
                disabled={!isStatusChangeAllowed}
              >
                <Option value={1} title={textMap.available}>
                  {textMap.available}
                </Option>
                <Option
                  value={2}
                  style={{
                    color: "#F55538",
                  }}
                  title={textMap.unavailableToday}
                >
                  {textMap.unavailableToday}
                </Option>
                <Option
                  value={3}
                  style={{
                    color: "#F55538",
                  }}
                  title={textMap.unavailableIndefinitely}
                >
                  {textMap.unavailableIndefinitely}
                </Option>
              </Select>
            </div>
          </Col>
        )}
        <Col className={`checkbox-container ${showCheckbox ? "show" : ""}`}>
          <Checkbox
            checked={
              !!checkedModifiers.find(
                item => item.modifierID === modifier.modifierID,
              )
            }
            onChange={() => onChangeModifierCheckbox(index)}
          />
        </Col>
      </Row>
    )
  }

  const { mustEditThroughMenu } = useInventoryContext()

  // Show loading state when isLoading is true
  if (isLoading) {
    return (
      <Card
        className={`${commonStyles.menuTableCard} ${commonStyles.right}`}
        title={
          <Row>
            <Col>{textMap.header}</Col>
          </Row>
        }
      >
        <ModifierListLoading showAvailability={!disableAvailability} />
      </Card>
    )
  }

  return (
    <Card
      data-test="card-container"
      className={`${commonStyles.menuTableCard} ${commonStyles.right}`}
      title={
        <Row align="middle" justify="space-between">
          <Col>{textMap.header}</Col>
          <Col>
            <Row align="middle" gutter={8}>
              <Col>
                {!isSellingTimeStructure && showEditBtn && (
                  <Button
                    size="small"
                    icon={<EditableFilled />}
                    onClick={() => onClickEditModifier()}
                    disabled={!isEditAllowed}
                  >
                    {textMap.edit}
                  </Button>
                )}
              </Col>
              <Col>
                <Dropdown
                  data-testid="menu-button-dropdown"
                  menu={{
                    items: [
                      {
                        key: selectMultiple,
                        label: textMap.selectMultiple,
                        onClick: () => onToggleShowCheckboxModifier(true),
                      },
                      {
                        key: rearrange,
                        label: textMap.rearrange,
                        onClick: () => onToggleShowSortOrderModifier(true),
                      },
                    ],
                  }}
                  trigger={["click"]}
                >
                  <MoreVerticalUniversal />
                </Dropdown>
              </Col>
            </Row>
          </Col>
        </Row>
      }
    >
      <SortableItemList
        items={modifiers}
        renderRowContent={renderRow}
        useDragHandle
        itemSize={rowHeight}
        draggable={getUCMConfig("enableDraggableList") && !mustEditThroughMenu}
        onSort={({ newList }) => onSort(newList)}
      />
    </Card>
  )
}

export default ModifierList
